import { Injectable } from '@angular/core';
import {
  PUSHER_SUBSCRIPTIONS,
  IWithdraw,
  IBet,
  IDraw,
  CHAT_CHANNEL,
  IChatMessage,
  IGameActivity,
  IDeposit,
  PUSHER_CHANNELS,
  IEthBlock,
} from '@libs/interfaces';
import { merge, Observable, Subject, Subscription, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  addBetDraw,
  addEthBlock,
  addLastBettingBet,
  addPendingBet,
  drawEvaluated,
  removePendingBet,
} from '@libs/store/betting/actions';
import { noop } from 'lodash';
import { addNotification } from '@libs/store/notifications/actions';
import Pusher, { Channel } from 'pusher-js';
import { addGamesActivity } from '@libs/store/game/actions';
import { AuthService } from '@libs/app.services/landing/auth.service';
Pusher.logToConsole = false;
@Injectable({
  providedIn: 'root',
})
export class PusherUserService {
  /**
   * Pusher app ID for authentication
   */
  private PUSHER_ID = 'facdb91483e6bf69b2e2';

  /**
   * Channel for chat messages
   */
  private chat_channel: Channel;

  /**
   * Channel for casino events
   */
  private casinoChannel: Channel;

  /**
   * Channel for betting events
   */
  private betting_channel: Channel;

  /**
   * Channel for user-specific events
   */
  private user_channel: Channel;

  /**
   * Channel for events broadcast to all users
   */
  private allUsers_channel: Channel;

  /**
   * Channel for LDB events
   */
  private ldb_channel: Channel;

  /**
   * Main Pusher client instance
   */
  private pusher: Pusher;

  /**
   * Subject that emits when a withdrawal is cancelled
   */
  withdrawCancelled = new Subject<IWithdraw>();

  /**
   * Subject that emits when a withdrawal is approved
   */
  withdrawApproved = new Subject<IWithdraw>();

  /**
   * Subject that emits when a withdrawal is pending
   */
  withdrawPending = new Subject<IWithdraw>();

  /**
   * Subject that emits when a crypto deposit is received
   */
  depositCryptoReceived = new Subject<IDeposit>();

  /**
   * Subject that emits when a deposit is approved
   */
  depositApproved = new Subject<IDeposit | unknown>();

  /**
   * Subject that emits when a deposit is cancelled
   */
  depositCancelled = new Subject<IDeposit>();

  /**
   * Subject that emits when a deposit is pending
   */
  depositPending = new Subject<IDeposit>();

  /**
   * Subject that emits when a draw is evaluated
   */
  drawEvaluated = new Subject<IDraw>();

  /**
   * Subject that emits when user data changes
   */
  userChangeData = new Subject();

  /**
   * Subject that emits when user ticket changes
   */
  userChangeTicket = new Subject();

  /**
   * Subject that emits when balance is updated
   */
  updatedBalance = new Subject();

  /**
   * Subject that emits for winning bets
   */
  winnersBet = new Subject();

  /**
   * Subject that emits when a new chat message is received
   */
  onNewChatMessage = new Subject<IChatMessage>();

  /**
   * Subject that emits when a user is blocked
   */
  onBlockedUser = new Subject();

  /**
   * Subject that emits when a user is banned from chat
   */
  onUserBannedChat = new Subject();

  /**
   * Subscription for managing Pusher event subscriptions
   */
  private subscription: Subscription;

  constructor(
    private store: Store,
    private auth: AuthService,
  ) {}

  /**
   * Initializes the Pusher service with the given user ID and token.
   * @param user_id - The user ID.
   */
  init(user_id: string) {
    if (user_id) {
      this.onUserLogged(user_id);
    } else {
      this.onNotLogged();
    }
  }

  /**
   * Initializes Pusher client for non-logged in users
   */
  onNotLogged() {
    this.pusher = new Pusher(this.PUSHER_ID, {
      cluster: 'sa1',
    });
    this.allUsers_channel = this.pusher.subscribe(PUSHER_CHANNELS.ALL_USERS);
    this.subscription?.unsubscribe();
    this.subscription = this.listenAllUsers().subscribe();
  }

  /**
   * Initializes Pusher client for logged in users
   * @param user_id - The user ID to subscribe to
   */
  onUserLogged(user_id: string) {
    this.pusher = new Pusher(this.PUSHER_ID, {
      cluster: 'sa1',
      userAuthentication: {
        endpoint: 'https://api.lbets.io/pusher/user-auth',
        transport: 'ajax',
        headers: {
          Authorization: `Bearer ${this.auth.token()}`,
        },
      },
    });
    this.user_channel = this.pusher.subscribe(`${user_id}`);
    this.pusher.signin();
    this.allUsers_channel = this.pusher.subscribe(PUSHER_CHANNELS.ALL_USERS);
    this.subscription?.unsubscribe();
    this.subscription = merge(this.listenAllUsers(), this.listenUser()).subscribe();
  }

  /**
   * Sets up listeners for events broadcast to all users
   */
  listenAllUsers() {
    return merge(this.drawEvaluatedEvent(), this.someUserBannedChatEvent());
  }

  /**
   * Handles draw evaluated events
   */
  private drawEvaluatedEvent() {
    return this.allUsers(PUSHER_SUBSCRIPTIONS.DRAW_EVALUATED).pipe(
      tap((draw: IDraw) => {
        this.store.dispatch(drawEvaluated({ draw }));
        this.drawEvaluated.next(draw);
      }),
    );
  }

  /**
   * Handles user banned from chat events
   */
  private someUserBannedChatEvent() {
    return this.allUsers(PUSHER_SUBSCRIPTIONS.SOME_USER_BANNED_CHAT).pipe(tap(() => this.onUserBannedChat.next(true)));
  }

  /**
   * Listens to various Pusher events and dispatches corresponding actions.
   * @returns An observable that emits when any of the subscribed events occur.
   */
  private listenUser() {
    const bicho = [
      this.subscribe_user(PUSHER_SUBSCRIPTIONS.NEW_BET).pipe(
        tap((bet: any) => this.store.dispatch(addPendingBet({ bet }))),
        tap((bet: any) => this.store.dispatch(addBetDraw({ bet }))),
      ),
      this.subscribe_user(PUSHER_SUBSCRIPTIONS.REMOVE_BET).pipe(
        tap((bet: any) => this.store.dispatch(removePendingBet({ bet }))),
      ),
      this.subscribe_user(PUSHER_SUBSCRIPTIONS.WINNER_BET).pipe(tap((data: any) => this.winnersBet.next(data))),
      this.subscribe_user(PUSHER_SUBSCRIPTIONS.USER_CHANGE_TICKET).pipe(
        tap((data: any) => this.userChangeTicket.next(data)),
      ),
    ];
    const casino = [this.subscribe_user(PUSHER_SUBSCRIPTIONS.GAME_CLOSE_BY_PROVIDER)];
    const base = [
      this.subscribe_user(PUSHER_SUBSCRIPTIONS.USER_BLOCKED).pipe(tap(() => this.onBlockedUser.next(true))),
      this.subscribe_user(PUSHER_SUBSCRIPTIONS.WITHDRAW_CANCELLED).pipe(
        tap((data: any) => this.withdrawCancelled.next(data)),
      ),
      this.subscribe_user(PUSHER_SUBSCRIPTIONS.WITHDRAW_APPROVED).pipe(
        tap((data: any) => this.withdrawApproved.next(data)),
      ),
      this.subscribe_user(PUSHER_SUBSCRIPTIONS.WITHDRAW_PENDING).pipe(
        tap((data: any) => this.withdrawPending.next(data)),
      ),
      this.subscribe_user(PUSHER_SUBSCRIPTIONS.USER_CHANGE_DATA).pipe(
        tap((data: any) => this.userChangeData.next(data)),
      ),

      this.subscribe_user(PUSHER_SUBSCRIPTIONS.PERSONAL_NOTIFICATION).pipe(tap(data => this.notify(data))),
      this.subscribe_user(PUSHER_SUBSCRIPTIONS.PLATFORM_NOTIFICATION).pipe(tap(data => this.notify(data))),
      this.subscribe_user(PUSHER_SUBSCRIPTIONS.UPDATE_USER_BALANCE).pipe(
        tap((data: any) => this.updatedBalance.next(data)),
      ),
      this.subscribe_user(PUSHER_SUBSCRIPTIONS.DEPOSIT_APPROVED).pipe(
        tap((data: IDeposit) => this.depositApproved.next(data)),
        tap((data: IDeposit) => this.ondeposit(data)),
      ),
      this.subscribe_user(PUSHER_SUBSCRIPTIONS.DEPOSIT_CRYPTO_RECEIVED).pipe(
        tap((data: any) => this.depositCryptoReceived.next(data)),
      ),
      this.subscribe_user(PUSHER_SUBSCRIPTIONS.DEPOSIT_CANCELLED).pipe(
        tap((data: IDeposit) => this.depositCancelled.next(data)),
      ),
      this.subscribe_user(PUSHER_SUBSCRIPTIONS.DEPOSIT_PENDING).pipe(
        tap((data: IDeposit) => this.depositPending.next(data)),
      ),
    ];
    return merge(...base, ...casino, ...bicho);
  }

  /**
   * Starts listening to betting events.
   */
  startListenBetting() {
    if (!this.betting_channel) {
      this.betting_channel = this.pusher.subscribe(PUSHER_CHANNELS.BETTING);
      this.betting_channel.bind(PUSHER_SUBSCRIPTIONS.NEW_BET, (bet: Partial<IBet>) =>
        this.store.dispatch(addLastBettingBet({ bet })),
      );
    } else this.betting_channel = this.pusher.subscribe(PUSHER_CHANNELS.BETTING);
  }

  /**
   * Stops listening to betting events.
   */
  stopListenBetting() {
    this.betting_channel?.unsubscribe();
  }

  /**
   * Starts listening to LDB events.
   */
  startListenLdb() {
    if (!this.ldb_channel) {
      this.ldb_channel = this.pusher.subscribe(PUSHER_CHANNELS.LDB);
      this.ldb_channel.bind(PUSHER_SUBSCRIPTIONS.ETH_BLOCK, (block: IEthBlock) =>
        this.store.dispatch(addEthBlock({ block })),
      );
    } else this.ldb_channel = this.pusher.subscribe(PUSHER_CHANNELS.LDB);
  }

  /**
   * Stops listening to LDB events.
   */
  stopListenLdb() {
    this.ldb_channel?.unsubscribe();
  }

  /**
   * Starts listening to casino events.
   */
  startListenCasino() {
    if (!this.casinoChannel) {
      this.casinoChannel = this.pusher.subscribe(PUSHER_CHANNELS.CASINO);
      this.casinoChannel.bind(PUSHER_SUBSCRIPTIONS.GAME_ACTIVITY, (activity: IGameActivity) =>
        this.store.dispatch(addGamesActivity({ activity })),
      );
    } else this.casinoChannel = this.pusher.subscribe(PUSHER_CHANNELS.CASINO);
  }

  /**
   * Stops listening to casino events.
   */
  stopListenCasino() {
    this.casinoChannel?.unsubscribe();
  }

  /**
   * Subscribes to a chat channel and listens for new chat messages.
   * @param channel - The chat channel to subscribe to.
   */
  subscribeChatChannel(channel: CHAT_CHANNEL) {
    if (!this.chat_channel) {
      this.chat_channel = this.pusher.subscribe(channel);
      this.chat_channel.bind(PUSHER_SUBSCRIPTIONS.CHAT_MESSAGE, message => {
        this.onNewChatMessage.next(message);
      });
    } else this.chat_channel = this.pusher.subscribe(channel);
  }

  /**
   * Stops listening to chat channel messages.
   */
  stopListenChatChannel() {
    this.chat_channel?.unbind(PUSHER_SUBSCRIPTIONS.CHAT_MESSAGE, noop);
    this.chat_channel?.unsubscribe();
  }

  /**
   * Dispatches a notification action with the given data.
   * @param data - The notification data.
   */
  private notify(data) {
    this.store.dispatch(addNotification({ notification: data }));
  }

  /**
   * Subscribes to a user-specific Pusher event.
   * @param event - The Pusher event to subscribe to.
   * @returns An observable that emits the event data.
   */
  private subscribe_user(event: PUSHER_SUBSCRIPTIONS) {
    return new Observable(observer => this.user_channel.bind(event, data => observer.next(data)));
  }

  /**
   * Subscribes to an all-users Pusher event.
   * @param event - The Pusher event to subscribe to.
   * @returns An observable that emits the event data.
   */
  private allUsers(event: PUSHER_SUBSCRIPTIONS) {
    return new Observable(observer => this.allUsers_channel.bind(event, data => observer.next(data)));
  }

  /**
   * Handles deposit events by pushing data to the data layer.
   * @param deposit - The deposit data.
   */
  private ondeposit(deposit: IDeposit) {
    (window as any).dataLayer.push({
      value: deposit.amount,
      event: 'deposito',
    });
  }
}
